import {
  forwardRef,
  useRef,
  useImperativeHandle,
  type ComponentProps,
  type ReactNode,
} from "react";
import { dropdownMenuStyles, dropdownPositionStyles } from "../helper";
import type { VariantProps } from "class-variance-authority";
import type { IconType } from "react-icons";
import { cn } from "@core/utils/cn";
import { assets } from "@core/assets";
import useClickOutside from "@core/utils/clickOutside/useClickOutside";

export type DropdownOption = string | any;

type SingleOptionProps = ComponentProps<"div"> &
  VariantProps<typeof dropdownMenuStyles> & {
    title: string;
    isOpen: boolean;
    label?: string;
    toggleAction: (open: boolean) => void;
    options: DropdownOption[];
    selectedOption: DropdownOption | null;
    selectAction: (option: DropdownOption) => void;
    displayKey?: keyof any;
    labelKey?: keyof any;
    icon?: IconType | string;
    children?: ReactNode;
    renderOption?: (option: DropdownOption) => ReactNode;
    disabled?: boolean;
  };

const SingleOption = forwardRef<HTMLDivElement, SingleOptionProps>(
  (
    {
      variant,
      position = "bottomleft",
      title,
      isOpen = false,
      toggleAction,
      className,
      options,
      selectedOption,
      selectAction,
      displayKey = "name",
      labelKey = "label",
      icon = "icon",
      renderOption,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => triggerRef.current as HTMLDivElement);

    useClickOutside([triggerRef, menuRef], () => {
      if (isOpen) toggleAction(false);
    });

    const toggle = () => {
      if (!disabled) toggleAction(!isOpen);
    };

    const isOptionSelected = (option: DropdownOption): boolean => {
      if (!selectedOption) return false;

      const optionValue =
        typeof option === "string" ? option : option[displayKey];

      const selectedValue =
        typeof selectedOption === "string"
          ? selectedOption
          : selectedOption[displayKey];

      return optionValue === selectedValue;
    };

    const IconMenu = isOpen ? assets["chevron-up"] : assets["chevron-down"];
    const hasFullWidth = className?.includes("w-full");

    return (
      <div className={cn("relative", hasFullWidth ? "w-full" : "w-fit")}>
        <div
          ref={triggerRef}
          onMouseDown={(e) => e.preventDefault()}
          onClick={toggle}
          className={cn(
            dropdownMenuStyles({ variant, position }),
            "min-w-0",
            disabled ? "bg-neutral-50 cursor-not-allowed" : "",
            className,
          )}
          {...props}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={cn(
                "text-sm truncate",
                !selectedOption ? "text-neutral-300" : "text-neutral-950",
                disabled && "text-neutral-300",
              )}
            >
              {!selectedOption
                ? title
                : typeof selectedOption === "string"
                  ? selectedOption
                  : selectedOption[displayKey]}
            </span>
          </div>

          <div className="flex-shrink-0">
            <IconMenu className="size-5" strokeWidth={1.6} />
          </div>
        </div>

        {isOpen && (
          <div
            ref={menuRef}
            className={cn(dropdownPositionStyles({ position }))}
          >
            {options.map((option, index) => {
              const isSelected = isOptionSelected(option);

              return (
                <button
                  key={index}
                  type="button"
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md text-sm",
                    "transition-colors",
                    "hover:bg-neutral-50",
                    isSelected ? "text-primary-800" : "text-neutral-950",
                  )}
                  onClick={() => {
                    selectAction(option);
                    toggleAction(false);
                  }}
                >
                  {renderOption ? (
                    renderOption(option)
                  ) : (
                    <div className="flex items-start gap-2">
                      {typeof icon === "string"
                        ? option[icon]
                        : typeof icon === "function"
                          ? (() => {
                              const IconComponent = icon as IconType;
                              return <IconComponent className="mt-0.5" />;
                            })()
                          : null}

                      <div className="min-w-0">
                        <div className="truncate capitalize">
                          {typeof option === "string"
                            ? option
                            : option[displayKey]}
                        </div>

                        {typeof option === "object" && option[labelKey] && (
                          <div className="text-[10px] text-neutral-400 mt-0.5 truncate">
                            {option[labelKey]}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

SingleOption.displayName = "SingleOption";

export default SingleOption;
